<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CompanySelector from 'dashboard/components-next/Companies/CompanySelector.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const { currentAccount, isCloudFeatureEnabled } = useAccount();

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  CITY: { field: 'additionalAttributes.city' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  BIO: { field: 'additionalAttributes.description' },
  COMPANY_NAME: { field: 'additionalAttributes.companyName' },
};

const TALKVIA_PF_FIELD_CONFIG = [
  {
    key: 'cpf',
    label: 'CPF',
    placeholder: '000.000.000-00',
  },
  {
    key: 'rg',
    label: 'RG',
    placeholder: '00.000.000-0',
  },
  {
    key: 'matricula',
    label: 'Matrícula',
    placeholder: 'Matrícula do cliente',
  },
  {
    key: 'data_nascimento',
    label: 'Data de nascimento',
    placeholder: 'dd/mm/aaaa',
  },
  {
    key: 'nome_mae',
    label: 'Nome da mãe',
    placeholder: 'Nome completo da mãe',
  },
];

const TALKVIA_PJ_FIELD_CONFIG = [
  {
    key: 'razao_social',
    label: 'Razão social',
    placeholder: 'Nome empresarial',
  },
  {
    key: 'nome_fantasia',
    label: 'Nome fantasia',
    placeholder: 'Nome fantasia',
  },
];

const TALKVIA_SECTION_TITLE = 'Dados Talkvia';
const ADDRESS_SECTION_TITLE = 'Endereço';
const ZIP_CODE_LOADING_TEXT = 'Consultando CEP...';
const ADDRESS_FIELD_CONFIG = [
  {
    key: 'zipCode',
    label: 'CEP',
    placeholder: '00000-000',
  },
  {
    key: 'addressLine',
    label: 'Endereço',
    placeholder: 'Rua, avenida ou logradouro',
  },
  {
    key: 'addressNumber',
    label: 'Número',
    placeholder: 'Número',
  },
  {
    key: 'addressComplement',
    label: 'Complemento',
    placeholder: 'Apartamento, bloco, sala',
  },
  {
    key: 'neighborhood',
    label: 'Bairro',
    placeholder: 'Bairro',
  },
  {
    key: 'state',
    label: 'UF',
    placeholder: 'UF',
  },
];

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TELEGRAM: 'i-ri-telegram-fill',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const getDefaultState = () => ({
  id: 0,
  name: '',
  email: '',
  companyId: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    zipCode: '',
    addressLine: '',
    addressNumber: '',
    addressComplement: '',
    neighborhood: '',
    state: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      telegram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
  customAttributes: {},
});

const state = reactive(getDefaultState());
const isFetchingZipCode = ref(false);
const isFetchingCnpj = ref(false);

const isEmpresa = computed(() => state.customAttributes?.tipo_pessoa === 'pj');

const talkviaFieldConfig = computed(() =>
  isEmpresa.value ? TALKVIA_PJ_FIELD_CONFIG : TALKVIA_PF_FIELD_CONFIG
);

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);
const hasCompaniesFeature = computed(
  () =>
    currentAccount.value?.id && isCloudFeatureEnabled(FEATURE_FLAGS.COMPANIES)
);
const showCompanySelector = computed(
  () =>
    hasCompaniesFeature.value &&
    (Boolean(state.companyId) || !state.additionalAttributes.companyName)
);

const emitContactUpdate = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  const { firstName, lastName, ...stateWithoutNames } = state;
  emit('update', stateWithoutNames);
};

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    companyId = '',
    additionalAttributes = {},
    customAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    zipCode = '',
    addressLine = '',
    addressNumber = '',
    addressComplement = '',
    neighborhood = '',
    state: addressState = '',
    socialTelegramUserName = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  const telegramUsername =
    socialProfiles?.telegram || socialTelegramUserName || '';

  Object.assign(state, {
    id,
    name,
    companyId: companyId || '',
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      zipCode,
      addressLine,
      addressNumber,
      addressComplement,
      neighborhood,
      state: addressState,
      socialProfiles: {
        ...socialProfiles,
        telegram: telegramUsername,
      },
    },
    customAttributes: {
      ...customAttributes,
    },
  });
};

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const editDetailsForm = computed(() =>
  Object.keys(FORM_CONFIG).map(key => ({
    key,
    placeholder: t(
      `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`
    ),
  }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

const emitUpdatedState = () => {
  const { firstName, lastName, ...stateWithoutNames } = state;
  emit('update', stateWithoutNames);
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      await emitContactUpdate();
    },
  });
};

const getCustomAttributeBinding = key =>
  computed({
    get: () => state.customAttributes?.[key]?.toString() || '',
    set: async value => {
      state.customAttributes[key] = value;
      const isFormValid = await v$.value.$validate();
      if (isFormValid) emitUpdatedState();
    },
  });

const getAdditionalAttributeBinding = key =>
  computed({
    get: () => state.additionalAttributes?.[key]?.toString() || '',
    set: async value => {
      state.additionalAttributes[key] = value;
      const isFormValid = await v$.value.$validate();
      if (isFormValid) emitUpdatedState();
    },
  });

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emitUpdatedState();
};

const cleanZipCode = value => String(value || '').replace(/\D/g, '');

const lookupZipCode = async () => {
  const zipCode = cleanZipCode(state.additionalAttributes.zipCode);
  if (zipCode.length !== 8) return;

  isFetchingZipCode.value = true;
  try {
    const response = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`);
    const data = await response.json();
    if (!response.ok || data.erro) throw new Error('CEP não encontrado');

    Object.assign(state.additionalAttributes, {
      zipCode,
      addressLine: data.logradouro || state.additionalAttributes.addressLine,
      neighborhood: data.bairro || state.additionalAttributes.neighborhood,
      city: data.localidade || state.additionalAttributes.city,
      state: data.uf || state.additionalAttributes.state,
      addressComplement:
        data.complemento || state.additionalAttributes.addressComplement,
      countryCode: state.additionalAttributes.countryCode || 'BR',
      country: state.additionalAttributes.country || 'Brazil',
    });
    emitUpdatedState();
  } catch {
    // Keep typed address when ViaCEP cannot locate the ZIP code.
  } finally {
    isFetchingZipCode.value = false;
  }
};

const cleanCnpj = value => String(value || '').replace(/\D/g, '');

const toggleTipoPessoa = () => {
  state.customAttributes.tipo_pessoa = isEmpresa.value ? 'pf' : 'pj';
  emitUpdatedState();
};

const lookupCnpj = async () => {
  const cnpj = cleanCnpj(state.customAttributes.cnpj);
  if (cnpj.length !== 14) return;

  isFetchingCnpj.value = true;
  try {
    const response = await fetch(
      `https://brasilapi.com.br/api/cnpj/v1/${cnpj}`
    );
    if (!response.ok) throw new Error('CNPJ não encontrado');
    const data = await response.json();

    state.customAttributes.razao_social = data.razao_social || '';
    state.customAttributes.nome_fantasia = data.nome_fantasia || '';

    if (data.cep) {
      const cep = String(data.cep).replace(/\D/g, '');
      Object.assign(state.additionalAttributes, {
        zipCode: cep,
        addressLine: data.logradouro || state.additionalAttributes.addressLine,
        addressNumber: data.numero || state.additionalAttributes.addressNumber,
        addressComplement:
          data.complemento || state.additionalAttributes.addressComplement,
        neighborhood: data.bairro || state.additionalAttributes.neighborhood,
        city: data.municipio || state.additionalAttributes.city,
        state: data.uf || state.additionalAttributes.state,
        countryCode: 'BR',
        country: 'Brazil',
      });
    }

    if (data.razao_social && !state.firstName) {
      state.name = data.nome_fantasia || data.razao_social;
      const { firstName, lastName } = splitName(state.name);
      state.firstName = firstName;
      state.lastName = lastName;
    }

    emitUpdatedState();
    useAlert('Dados do CNPJ carregados com sucesso.');
  } catch {
    useAlert('CNPJ não encontrado ou inválido.');
  } finally {
    isFetchingCnpj.value = false;
  }
};

const handleCompanySelection = async ({ id, name }) => {
  state.companyId = id || '';
  state.additionalAttributes.companyName = name || '';
  await emitContactUpdate();
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, getDefaultState());
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col items-start gap-2">
      <span v-if="showTitle" class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE') }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <template v-for="item in editDetailsForm" :key="item.key">
          <ComboBox
            v-if="item.key === 'COUNTRY'"
            v-model="state.additionalAttributes.countryCode"
            :options="countryOptions"
            :placeholder="item.placeholder"
            class="[&>div>button]:h-8"
            :class="{
              '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                !isDetailsView,
              '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
            }"
            @update:model-value="handleCountrySelection"
          />
          <PhoneNumberInput
            v-else-if="item.key === 'PHONE_NUMBER'"
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :show-border="isDetailsView"
          />
          <CompanySelector
            v-else-if="item.key === 'COMPANY_NAME' && showCompanySelector"
            :model-value="state.companyId"
            :selected-name="state.additionalAttributes.companyName"
            :is-details-view="isDetailsView"
            @select="handleCompanySelection"
          />
          <Input
            v-else
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :message-type="getMessageType(item.key)"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${
              !isDetailsView
                ? '[&:not(.error,.focus)]:!outline-transparent'
                : ''
            }`"
            class="w-full"
            @input="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
            @blur="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
          />
        </template>
      </div>
    </div>
    <div class="flex flex-col items-start gap-2">
      <div class="flex items-center justify-between w-full py-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ TALKVIA_SECTION_TITLE }}
        </span>
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition"
          :class="
            isEmpresa
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
          "
          @click="toggleTipoPessoa"
        >
          <span
            class="size-3.5"
            :class="isEmpresa ? 'i-lucide-building-2' : 'i-lucide-user'"
          />
          {{ isEmpresa ? 'Empresa (CNPJ)' : 'Pessoa Física (CPF)' }}
        </button>
      </div>

      <div v-if="isEmpresa" class="flex w-full items-end gap-2">
        <Input
          v-model="getCustomAttributeBinding('cnpj').value"
          label="CNPJ"
          placeholder="00.000.000/0000-00"
          :custom-input-class="`h-8 !pt-1 !pb-1 ${
            !isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''
          }`"
          class="flex-1"
          @blur="lookupCnpj"
        />
        <Button
          icon="i-lucide-search"
          size="sm"
          color="slate"
          variant="outline"
          :is-loading="isFetchingCnpj"
          class="mb-0.5 h-8 shrink-0"
          @click="lookupCnpj"
        />
      </div>

      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          v-for="item in talkviaFieldConfig"
          :key="item.key"
          v-model="getCustomAttributeBinding(item.key).value"
          :label="item.label"
          :placeholder="item.placeholder"
          :custom-input-class="`h-8 !pt-1 !pb-1 ${
            !isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''
          }`"
          class="w-full"
        />
      </div>

      <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
      <span v-if="isFetchingCnpj" class="text-xs text-n-slate-10">
        Consultando CNPJ...
      </span>
    </div>
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ ADDRESS_SECTION_TITLE }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          v-for="item in ADDRESS_FIELD_CONFIG"
          :key="item.key"
          v-model="getAdditionalAttributeBinding(item.key).value"
          :label="item.label"
          :placeholder="item.placeholder"
          :custom-input-class="`h-8 !pt-1 !pb-1 ${
            !isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''
          }`"
          class="w-full"
          @blur="item.key === 'zipCode' && lookupZipCode()"
        />
        <Input
          v-model="getAdditionalAttributeBinding('city').value"
          label="Cidade"
          placeholder="Cidade"
          :custom-input-class="`h-8 !pt-1 !pb-1 ${
            !isDetailsView ? '[&:not(.error,.focus)]:!outline-transparent' : ''
          }`"
          class="w-full"
        />
      </div>
      <span v-if="isFetchingZipCode" class="text-xs text-n-slate-10">
        {{ ZIP_CODE_LOADING_TEXT }}
      </span>
    </div>
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :size="item.placeholder.length"
            @input="emitUpdatedState"
          />
        </div>
      </div>
    </div>
  </div>
</template>
